import React, { useState, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { UserAddOutlined } from '@ant-design/icons';
import { Input } from '../ui';
import { createInvite, INVITE_CONSENT_VERSION, maskPhone } from '../../api/invite';
import { useToastContext } from '../../context/ToastContext';
import '../ui/Alert/modalAlert.css';
import './styles/inviteStudentModal.css';

interface InviteStudentModalProps {
  visible: boolean;
  studentId: string;
  studentName?: string;
  /** True when a pending invite exists; sending replaces it. */
  isResend?: boolean;
  onClose: () => void;
  /** Called after the invite is sent, so the card can show it as pending. */
  onSent?: () => void;
}

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

export const InviteStudentModal: React.FC<InviteStudentModalProps> = ({
  visible,
  studentId,
  studentName,
  isResend = false,
  onClose,
  onSent,
}) => {
  const { addToast } = useToastContext();
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible) {
      setPhone('');
      setConsent(false);
      setError(undefined);
    }
  }, [visible]);

  if (!visible) return null;

  const firstName = studentName?.split(' ')[0] || 'your child';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!INDIAN_MOBILE.test(phone)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    if (!consent) {
      setError('Please confirm your consent to send the invite');
      return;
    }

    try {
      setSubmitting(true);
      const invite = await createInvite({
        phoneNumber: `+91${phone}`,
        targetId: studentId,
        consentAccepted: true,
        consentVersion: INVITE_CONSENT_VERSION,
      });
      addToast({
        type: 'success',
        message: `Invite sent on WhatsApp to ${maskPhone(invite.phoneNumber || `+91${phone}`)} · pending`,
      });
      onSent?.();
      onClose();
    } catch (err) {
      // Backend errors (already has access, number in use, rate limit, unreachable) carry a
      // user-facing message.
      const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to send invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-alert__overlay" onClick={submitting ? undefined : onClose}>
      <form className="modal-alert invite-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className="modal-alert__icon-container">
          <UserAddOutlined className="modal-alert__icon modal-alert__icon--info" />
        </div>

        <h2 className="modal-alert__title">{isResend ? `Resend ${firstName}'s invite` : `Invite ${firstName}`}</h2>
        <p className="modal-alert__description">
          Enter {firstName}'s own mobile number. We'll send them a WhatsApp message with a link to
          sign in to the Zeroh app. The link works once and expires in 48 hours.
          {isResend && ' The link sent earlier will stop working.'}
        </p>

        <Input
          type="tel"
          inputMode="numeric"
          autoFocus
          maxLength={10}
          placeholder="10-digit mobile number"
          leftIcon={<span className="invite-modal__prefix">+91</span>}
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value.replace(/\D/g, ''));
            setError(undefined);
          }}
          error={error}
          disabled={submitting}
        />

        {/* ZAP-81: the parent's consent is required and stored on the invite. */}
        <label className="invite-modal__consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setError(undefined);
            }}
            disabled={submitting}
          />
          <span>
            I am {firstName}'s parent or guardian and I consent to {firstName} using the Zeroh app,
            where they can see their trips and attendance, as described in the Zero privacy policy.
          </span>
        </label>

        <div className="modal-alert__footer">
          <button
            type="button"
            className="modal-alert__button modal-alert__button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-alert__button modal-alert__button--primary modal-alert__button--info"
            disabled={submitting || phone.length !== 10 || !consent}
          >
            {submitting ? 'Sending...' : isResend ? 'Resend Invite' : 'Send Invite'}
          </button>
        </div>
      </form>
    </div>
  );
};
